import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Icon } from '@cbrebuild/blocks';

import FileUploadAccordian from '../../file-upload-accordian';
import {
  LeaseAbstractionField,
  LeaseAbstractionFieldType,
} from '../../leaseAbstractionField';
import '../../lease-abstraction.scss';
import LeaseAbstractionContext from '../../contexts/leaseAbstractionContext';
import useFormState from '../../../../hooks/useFormState';
import Select from '../../../../nucleus/select/select';
import Tooltip from '../../../../nucleus/tooltip/tooltip';
import { updateCompData } from '../../../../redux/actions/voucher/comp-data-actions';
import { selectOutsideBrokerCommissions } from '../../../../redux/selectors/voucher-selectors';

const accountDrivenOptions = [
  { display_name: 'Yes', value: 'Yes' },
  { display_name: 'No', value: 'No' },
];

const GeneralInformation = ({ onExpand, storageKey }) => {
  const {
    modalFieldState,
    modalFieldChangeHandler,
    individualModalFieldChangeHandler,
    fieldErrorMap,
    validateLeaseAbstractionFields,
    showPageValidation,
    saveCompField,
    clearFieldError,
    isEnhancedFlow,
    isEarlyInvoice,
  } = useContext(LeaseAbstractionContext);

  const history = useHistory();
  const dispatch = useDispatch();
  const {
    compData, outsideBrokers, dealId, repRole,
  } = useSelector(state => ({
    compData: state.compData,
    outsideBrokers: selectOutsideBrokerCommissions(state),
    dealId: state.deal.deal?.id,
    repRole: state.deal.deal?.rep_role,
  }));

  const isTenantRepDeal = repRole === 'tenant';
  const isLandlordRepDeal = repRole === 'landlord';
  const showRequiredValidation = showPageValidation && !isEarlyInvoice;

  const { account_driven_deal, onStateChanged } = useFormState(
    { account_driven_deal: compData?.account_driven_deal },
    changes => dispatch(updateCompData(compData.id, changes)),
  );

  const handleEditOutsideBroker = () => {
    history.push(`/voucher/${dealId}/financials/allocations`);
  };

  return (
    <FileUploadAccordian accordianTitle="General Information" onExpand={onExpand} storageKey={storageKey}>
      {/* Legacy "Import Data to Voucher" modal flow — original 4 fields, no
          section headings, no Account Driven, no Outside Broker info. */}
      {!isEnhancedFlow && (
        <>
          <LeaseAbstractionField
            key="tenantRepresentCompany"
            fieldType={LeaseAbstractionFieldType.TEXT}
            fieldData={{
              data: modalFieldState.tenantRepresentCompany,
              label: 'Tenant Represented By',
              errorMessage: fieldErrorMap.tenantRepresentCompany,
              key: 'tenantRepresentCompany',
            }}
            fieldChangeHandler={(event) => {
              modalFieldChangeHandler('tenantRepresentCompany', { value: event.target.value });
            }}
            individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.target.value,
                parent_key: 'comp_info',
                child_key: modalFieldState.tenantRepresentCompany.trackingID,
                stateVariable: 'tenantRepresentCompany',
              });
            }}
          />
          <LeaseAbstractionField
            key="tenantRepresentAgent"
            fieldType={LeaseAbstractionFieldType.TEXT}
            fieldData={{
              data: modalFieldState.tenantRepresentAgent,
              label: 'Tenant Represented By Agent',
              errorMessage: fieldErrorMap.tenantRepresentAgent,
              key: 'tenantRepresentAgent',
            }}
            fieldChangeHandler={(event) => {
              modalFieldChangeHandler('tenantRepresentAgent', { value: event.target.value });
            }}
            individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.target.value,
                parent_key: 'comp_info',
                child_key: modalFieldState.tenantRepresentAgent.trackingID,
                stateVariable: 'tenantRepresentAgent',
              });
            }}
          />
          <LeaseAbstractionField
            key="landlordRepresentCompany"
            fieldType={LeaseAbstractionFieldType.TEXT}
            fieldData={{
              data: modalFieldState.landlordRepresentCompany,
              label: 'Landlord Represented By',
              errorMessage: fieldErrorMap.landlordRepresentCompany,
              key: 'landlordRepresentCompany',
            }}
            fieldChangeHandler={(event) => {
              modalFieldChangeHandler('landlordRepresentCompany', { value: event.target.value });
            }}
            individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.target.value,
                parent_key: 'comp_info',
                child_key: modalFieldState.landlordRepresentCompany.trackingID,
                stateVariable: 'landlordRepresentCompany',
              });
            }}
          />
          <LeaseAbstractionField
            key="landlordRepresentAgent"
            fieldType={LeaseAbstractionFieldType.TEXT}
            fieldData={{
              data: modalFieldState.landlordRepresentAgent,
              label: 'Landlord Represented By Agent',
              errorMessage: fieldErrorMap.landlordRepresentAgent,
              key: 'landlordRepresentAgent',
            }}
            fieldChangeHandler={(event) => {
              modalFieldChangeHandler('landlordRepresentAgent', { value: event.target.value });
            }}
            individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.target.value,
                parent_key: 'comp_info',
                child_key: modalFieldState.landlordRepresentAgent.trackingID,
                stateVariable: 'landlordRepresentAgent',
              });
            }}
          />
        </>
      )}

      {/* New enhanced split-pane flow — section headings, Account Driven, Outside Brokers */}
      {isEnhancedFlow && (
        <>
      <div className="acc-section-heading">Tenant Rep Info</div>

      {/* Row: Tenant Rep By | Account Driven */}
      <div className="acc-field-row">
        <div className="acc-field-col">
          <LeaseAbstractionField
            key="tenantRepresentCompany"
            fieldType={LeaseAbstractionFieldType.TEXT}
            isRequired={!isEarlyInvoice}
            disabled={isTenantRepDeal}
            fieldData={{
              data: modalFieldState.tenantRepresentCompany,
              label: 'Tenant Represented By',
              errorMessage: (!isTenantRepDeal && !modalFieldState.tenantRepresentCompany.value && showRequiredValidation) ? 'Tenant Represented By is required' : fieldErrorMap.tenantRepresentCompany,
              key: 'tenantRepresentCompany',
            }}
            fieldChangeHandler={(event) => {
              modalFieldChangeHandler('tenantRepresentCompany', {
                value: event.target.value,
              });
            }}
            onBlur={() => saveCompField('tenantRepresentCompany', modalFieldState.tenantRepresentCompany.value)}
            acceptFieldChangeHandler={(extractedVal) => {
              modalFieldChangeHandler('tenantRepresentCompany', { value: extractedVal }, 'single');
              clearFieldError('tenantRepresentCompany');
              saveCompField('tenantRepresentCompany', extractedVal);
            }}
            individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.target.value,
                parent_key: 'comp_info',
                child_key: modalFieldState.tenantRepresentCompany.trackingID,
                stateVariable: 'tenantRepresentCompany',
              });
            }}
            validateLeaseAbstractionFields={validateLeaseAbstractionFields}
          />
        </div>
        <div className="acc-field-col">
          <div className="file-modal-group-container">
            <div className="file-modal-group">
              <label className="acc-field-label">
                Account Driven
                <Tooltip
                  message={
                    <>
                      <strong>Account Driven</strong><br /><br />
                      Account Driven — Contractual strategic account relationship<br />
                      One-Off — Locally sourced, not awarded due to strategic account client contract
                    </>
                  }
                >
                  <Icon iconName="info-circle" />
                </Tooltip>
              </label>
              <Select
                defaultOption={accountDrivenOptions.find(o => o.value === account_driven_deal.value)}
                onChange={({ value }) => onStateChanged({
                  target: { value, name: 'account_driven_deal', type: 'text' },
                })}
                options={accountDrivenOptions}
              />
            </div>
          </div>
        </div>
      </div>

      <LeaseAbstractionField
        key="tenantRepresentAgent"
        fieldType={LeaseAbstractionFieldType.TEXT}
        isRequired={!isEarlyInvoice}
        fieldData={{
          data: modalFieldState.tenantRepresentAgent,
          label: 'Tenant Represented By Agent',
          errorMessage: (!modalFieldState.tenantRepresentAgent.value && showRequiredValidation) ? 'Tenant Represented By Agent is required' : fieldErrorMap.tenantRepresentAgent,
          key: 'tenantRepresentAgent',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('tenantRepresentAgent', {
            value: event.target.value,
          });
        }}
        onBlur={() => saveCompField('tenantRepresentAgent', modalFieldState.tenantRepresentAgent.value)}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('tenantRepresentAgent', { value: extractedVal }, 'single');
          clearFieldError('tenantRepresentAgent');
          saveCompField('tenantRepresentAgent', extractedVal);
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'comp_info',
            child_key: modalFieldState.tenantRepresentAgent.trackingID,
            stateVariable: 'tenantRepresentAgent',
          });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />

      {/* Outside Broker Info — read-only; edit navigates to Financials/Allocations */}
      {/* Only show brokers with Landlord/Represent or Tenant/Represent rep role AND a name */}
      {outsideBrokers && outsideBrokers.filter(b =>
        (b.voucher_broker_rep_role === 'Landlord/Represent' || b.voucher_broker_rep_role === 'Tenant/Represent') &&
        b.outside_broker_name
      ).length > 0 && (
        <div className="acc-outside-brokers">
          <div className="acc-outside-brokers__header">
            <span className="acc-section-heading">Outside Broker Info</span>
            <button
              type="button"
              className="acc-outside-brokers__edit-btn"
              title="Edit outside broker in Financials"
              onClick={handleEditOutsideBroker}
            >
              <Icon iconName="edit" />
            </button>
          </div>
          {outsideBrokers.filter(b =>
            (b.voucher_broker_rep_role === 'Landlord/Represent' || b.voucher_broker_rep_role === 'Tenant/Represent') &&
            b.outside_broker_name
          ).map((broker, i) => (
            <div key={broker.id || i} className="acc-outside-brokers__item">
              <div className="acc-outside-brokers__item-heading">{`Outside Broker ${i + 1}`}</div>
              <div className="acc-field-row">
                <div className="acc-field-col">
                  <div className="file-modal-group-container">
                    <div className="file-modal-group">
                      <label className="acc-field-label">Outside Broker Company</label>
                      <input
                        type="text"
                        className="acc-input-text"
                        readOnly
                        disabled
                        value={broker.vendor?.name || ''}
                      />
                    </div>
                  </div>
                </div>
                <div className="acc-field-col">
                  <div className="file-modal-group-container">
                    <div className="file-modal-group">
                      <label className="acc-field-label">Outside Broker</label>
                      <input
                        type="text"
                        className="acc-input-text"
                        readOnly
                        disabled
                        value={broker.outside_broker_name || ''}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="acc-section-heading">Landlord Rep Info</div>

      <LeaseAbstractionField
        key="landlordRepresentCompany"
        fieldType={LeaseAbstractionFieldType.TEXT}
        isRequired={!isEarlyInvoice}
        disabled={isLandlordRepDeal}
        fieldData={{
          data: modalFieldState.landlordRepresentCompany,
          label: 'Landlord Represented By',
          errorMessage: (!isLandlordRepDeal && !modalFieldState.landlordRepresentCompany.value && showRequiredValidation) ? 'Landlord Represented By is required' : fieldErrorMap.landlordRepresentCompany,
          key: 'landlordRepresentCompany',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('landlordRepresentCompany', {
            value: event.target.value,
          });
        }}
        onBlur={() => saveCompField('landlordRepresentCompany', modalFieldState.landlordRepresentCompany.value)}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('landlordRepresentCompany', { value: extractedVal }, 'single');
          clearFieldError('landlordRepresentCompany');
          saveCompField('landlordRepresentCompany', extractedVal);
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'comp_info',
            child_key: modalFieldState.landlordRepresentCompany.trackingID,
            stateVariable: 'landlordRepresentCompany',
          });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="landlordRepresentAgent"
        fieldType={LeaseAbstractionFieldType.TEXT}
        isRequired={!isEarlyInvoice}
        fieldData={{
          data: modalFieldState.landlordRepresentAgent,
          label: 'Landlord Represented By Agent',
          errorMessage: (!modalFieldState.landlordRepresentAgent.value && showRequiredValidation) ? 'Landlord Represented By Agent is required' : fieldErrorMap.landlordRepresentAgent,
          key: 'landlordRepresentAgent',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('landlordRepresentAgent', {
            value: event.target.value,
          });
        }}
        onBlur={() => saveCompField('landlordRepresentAgent', modalFieldState.landlordRepresentAgent.value)}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('landlordRepresentAgent', { value: extractedVal }, 'single');
          clearFieldError('landlordRepresentAgent');
          saveCompField('landlordRepresentAgent', extractedVal);
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'comp_info',
            child_key: modalFieldState.landlordRepresentAgent.trackingID,
            stateVariable: 'landlordRepresentAgent',
          });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
        </>
      )}
    </FileUploadAccordian>
  );
};

GeneralInformation.propTypes = {
  onExpand: PropTypes.func,
  storageKey: PropTypes.string,
};

GeneralInformation.defaultProps = {
  onExpand: () => {},
  storageKey: '',
};

export default GeneralInformation;
